import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import {
    ResponsiveContainer,
    ScatterChart,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    Line,
    Scatter,
    PieChart,
    Pie,
    Cell,
    Legend,
} from "recharts";

// Reuse existing components where possible (passing props)
import DemoVoltageGauge from "./DemoVoltageGauge";
import CurrentBar from "../../components/CurrentBar";
import CosfiMiniGauge from "../../components/CosfiMiniGauge";

// Use demo-specific components
import DemoWeeklyEnergyChart from "./DemoWeeklyEnergyChart";
import DemoAnalyzerRatioGauge from "./DemoAnalyzerRatioGauge";

// Helper for random number generation
function random(min, max) {
    return Math.random() * (max - min) + min;
}

function DemoReactiveEnergyPie({ importVal = 0, exportVal = 0 }) {
    const { t } = useTranslation();
    const data = [
        { name: t('demo.reactiveEnergyImport'), value: importVal },
        { name: t('demo.reactiveEnergyExport'), value: exportVal },
    ];
    const COLORS = ["#3B82F6", "#EF4444"];

    return (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">{t('demo.reactiveEnergyDist')}</h3>
            <div className="flex items-center justify-center h-72">
                <PieChart width={300} height={300}>
                    <Pie data={data} cx="50%" cy="60%" outerRadius={110} dataKey="value">
                        {data.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => `${v.toFixed(2)} kVArh`} />
                    <Legend layout="horizontal" align="center" verticalAlign="bottom" wrapperStyle={{ transform: "translateY(15px)" }} />
                </PieChart>
            </div>
        </div>
    );
}

function DemoActiveEnergyPie({ importVal = 0, exportVal = 0 }) {
    const { t } = useTranslation();
    const data = [
        { name: t('demo.activeEnergyImport'), value: importVal },
        { name: t('demo.activeEnergyExport'), value: exportVal },
    ];
    const COLORS = ["#10B981", "#F59E0B"];

    return (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">{t('demo.activeEnergyDist')}</h3>
            <div className="flex items-center justify-center h-72">
                <PieChart width={300} height={300}>
                    <Pie data={data} cx="50%" cy="60%" outerRadius={110} dataKey="value">
                        {data.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => `${v.toFixed(2)} kWh`} />
                    <Legend layout="horizontal" align="center" verticalAlign="bottom" wrapperStyle={{ transform: "translateY(15px)" }} />
                </PieChart>
            </div>
        </div>
    );
}

export default function DemoDashboard() {
    const { t } = useTranslation();
    const [data, setData] = useState(null);

    // Initial dummy data generation
    const generateData = () => {
        return {
            voltages: {
                VL1: random(215, 225), VL2: random(215, 225), VL3: random(215, 225),
                VL12: random(375, 385), VL23: random(375, 385), VL31: random(375, 385),
            },
            currents: {
                I1: random(10, 50), I2: random(10, 50), I3: random(10, 50),
            },
            cosfi: {
                CosFi1: random(0.95, 1.0), CosFi2: random(0.95, 1.0), CosFi3: random(0.95, 1.0),
                CosFiT: random(0.95, 1.0),
            },
            activePower: { PTotalImport: random(10000, 20000) }, // Watts
            reactivePower: { Q1: random(100, 500), Q2: random(100, 500), Q3: random(100, 500) }, // VAr
            activeEnergy: { ImportTotal: random(50000, 60000), ExportTotal: random(1000, 2000) }, // Wh? Usually backend sends raw values. Here assume Wh for display / 1000 -> kWh
            reactiveEnergyFull: { ImportTotal: random(5000, 10000), ExportTotal: random(100, 500) },
            ratios: { VTR: 1 },
            enductiveRatio: random(1, 5),
            capacitiveRatio: random(0, 2),
        };
    };

    useEffect(() => {
        // Initial set
        setData(generateData());

        // Update every 3 seconds to simulate realtime
        const interval = setInterval(() => {
            setData(generateData());
        }, 3000);

        return () => clearInterval(interval);
    }, []);

    if (!data) return <div className="p-10 text-center">Demo Yükleniyor...</div>;

    const totalCurrent = (data.currents.I1 + data.currents.I2 + data.currents.I3);
    const totalQ = data.reactivePower.Q1 + data.reactivePower.Q2 + data.reactivePower.Q3;

    return (
        <div className="min-h-screen bg-slate-100 p-6 space-y-6">
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">{t('demo.title')}</h1>
                    <p className="text-slate-500 text-sm">{t('demo.subtitle')}</p>
                </div>
                <div className="flex items-center gap-4">
                    <LanguageSwitcher />
                    <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-xs font-semibold">
                        {t('demo.liveData')}
                    </span>
                </div>
            </div>

            {/* POWER OVERVIEW */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-col items-center justify-center">
                    <h3 className="text-slate-500 font-medium mb-1">{t('demo.totalActivePower')}</h3>
                    <div className="text-2xl font-bold text-emerald-600">{(data.activePower.PTotalImport / 1000).toFixed(2)} kW</div>
                </div>
                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-col items-center justify-center">
                    <h3 className="text-slate-500 font-medium mb-1">{t('demo.totalReactivePower')}</h3>
                    <div className="text-2xl font-bold text-red-600">{(totalQ / 1000).toFixed(2)} kVAr</div>
                </div>
                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-col items-center justify-center">
                    <h3 className="text-slate-500 font-medium mb-1">{t('demo.totalApparentPower')}</h3>
                    <div className="text-2xl font-bold text-blue-600">
                        {(Math.sqrt(Math.pow(data.activePower.PTotalImport, 2) + Math.pow(totalQ, 2)) / 1000).toFixed(2)} kVA
                    </div>
                </div>
            </div>

            {/* VOLTAJ + AKIM */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">
                    {t('demo.phaseVoltages')}
                </h3>
                <div className="flex flex-col lg:flex-row gap-8">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 flex-1">
                        {["VL1", "VL2", "VL3"].map((key) => (
                            <DemoVoltageGauge
                                key={key}
                                label={key}
                                value={data.voltages[key]}
                                vtr={1}
                                isLine={false}
                            />
                        ))}
                        {["VL12", "VL23", "VL31"].map((key) => (
                            <DemoVoltageGauge
                                key={key}
                                label={key}
                                value={data.voltages[key]}
                                vtr={1}
                                isLine={true}
                            />
                        ))}
                    </div>
                    <div className="flex flex-col justify-end gap-2 w-full lg:w-auto">
                        <div className="flex flex-row items-end justify-center gap-4">
                            <CurrentBar label="I1" value={data.currents.I1} max={100} />
                            <CurrentBar label="I2" value={data.currents.I2} max={100} />
                            <CurrentBar label="I3" value={data.currents.I3} max={100} />
                            <CurrentBar label="IΣ" value={totalCurrent} max={300} />
                        </div>
                    </div>
                </div>
            </div>

            {/* COSFI */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <CosfiMiniGauge label="Cosφ L1" value={data.cosfi.CosFi1} />
                <CosfiMiniGauge label="Cosφ L2" value={data.cosfi.CosFi2} />
                <CosfiMiniGauge label="Cosφ L3" value={data.cosfi.CosFi3} />
                <CosfiMiniGauge label="Cosφ Σ" value={data.cosfi.CosFiT} />
            </div>

            {/* RATIOS */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <DemoAnalyzerRatioGauge type="end" title={t('demo.inductiveRatio')} value={data.enductiveRatio} />
                <DemoAnalyzerRatioGauge type="cap" title={t('demo.capacitiveRatio')} value={data.capacitiveRatio} />
            </div>

            {/* ENERGY PIE CHARTS */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <DemoActiveEnergyPie
                    importVal={data.activeEnergy.ImportTotal / 1000}
                    exportVal={data.activeEnergy.ExportTotal / 1000}
                />
                <DemoReactiveEnergyPie
                    importVal={data.reactiveEnergyFull.ImportTotal / 1000}
                    exportVal={data.reactiveEnergyFull.ExportTotal / 1000}
                />
            </div>

            {/* WEEKLY CHARTS & PQ */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <DemoWeeklyEnergyChart />


            </div>
        </div>
    );
}
